/**
 * 회원가입 페이지 — Clipflow v3.6 Glass + Neon
 * 이메일/비밀번호/닉네임, 비밀번호 강도, 약관 동의, 소셜 가입
 */
'use client'

import { useState, type FormEvent } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { ClipflowLogo } from '@/components/brand/ClipflowLogo'
import { V3Icon } from '@/components/icons/V3Icon'
import { useAuthStore } from '@/store/authStore'

export default function SignupPage() {
  const router = useRouter()
  const { signup, socialLogin, isLoading, error, clearError } = useAuthStore()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [agreePrivacy, setAgreePrivacy] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)
  const [emailTouched, setEmailTouched] = useState(false)

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
  const showEmailError = emailTouched && email.length > 0 && !isValidEmail(email)

  const getPasswordStrength = (pw: string) => {
    if (!pw) return { score: 0, label: '', color: 'transparent', tip: '' }
    let score = 0
    const checks = {
      length: pw.length >= 8,
      upper: /[A-Z]/.test(pw),
      number: /[0-9]/.test(pw),
      special: /[^A-Za-z0-9]/.test(pw),
    }
    if (checks.length) score++
    if (checks.upper) score++
    if (checks.number) score++
    if (checks.special) score++

    const missing: string[] = []
    if (!checks.length) missing.push('8자 이상')
    if (!checks.upper) missing.push('대문자')
    if (!checks.number) missing.push('숫자')
    if (!checks.special) missing.push('특수문자')
    const tip = missing.length > 0 ? `추가 필요: ${missing.join(', ')}` : '모든 조건 충족'

    if (score <= 1) return { score, label: '약함', color: 'var(--v36-error)', tip }
    if (score === 2) return { score, label: '보통', color: 'var(--v36-warn)', tip }
    if (score === 3) return { score, label: '강함', color: 'var(--v36-success)', tip }
    return { score, label: '매우 강함', color: 'var(--v36-cyan)', tip }
  }

  const strength = getPasswordStrength(password)
  const isTermsAgreed = agreeTerms && agreePrivacy

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setLocalError(null)
    clearError()

    if (!isValidEmail(email)) {
      setLocalError('올바른 이메일 형식이 아닙니다.')
      return
    }
    if (password !== confirmPassword) {
      setLocalError('비밀번호가 일치하지 않습니다.')
      return
    }
    if (strength.score < 2) {
      setLocalError('비밀번호 강도가 너무 약합니다. 8자 이상 + 대문자/숫자/특수문자를 사용하세요.')
      return
    }
    if (!isTermsAgreed) {
      setLocalError('이용약관과 개인정보 처리방침에 동의해주세요.')
      return
    }

    try {
      await signup(name, email, password)
      router.push('/projects')
    } catch {
      /* 스토어가 에러 관리 */
    }
  }

  const displayedError = localError || error

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        background: 'var(--v36-bg)',
        backgroundImage: 'var(--v36-noise)',
      }}
    >
      <div className="v36-fade-in" style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <Link href="/" aria-label="Clipflow 홈">
            <ClipflowLogo height={32} priority />
          </Link>
        </div>

        <div
          className="v36-glass-strong"
          style={{
            padding: 32,
            borderRadius: 18,
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <h1 className="v36-h2" style={{ margin: 0, marginBottom: 6 }}>
              무료로 시작하기
            </h1>
            <p style={{ fontSize: 13, color: 'var(--v36-ink-3)', margin: 0 }}>
              30초만에 가입하고 바로 첫 영상을 만들어보세요
            </p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {displayedError && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(255,77,79,0.10)',
                  border: '1px solid rgba(255,77,79,0.30)',
                  fontSize: 13,
                  color: 'var(--v36-error)',
                }}
              >
                <V3Icon name="bell" size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>{displayedError}</span>
              </div>
            )}

            <Field label="닉네임">
              <input
                type="text"
                placeholder="홍길동"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="v36-input"
              />
            </Field>

            <Field label="이메일" hint={showEmailError ? '올바른 이메일 형식이 아닙니다' : undefined}>
              <input
                type="email"
                placeholder="name@example.com"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                className="v36-input"
                style={{
                  borderColor: showEmailError ? 'var(--v36-error)' : undefined,
                }}
              />
            </Field>

            <Field label="비밀번호" hint={password ? strength.tip : undefined}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="8자 이상, 대문자/숫자/특수문자 포함"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="v36-input"
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? '숨기기' : '보기'}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 22,
                  color: 'var(--v36-ink-4)',
                  padding: 4,
                }}
              >
                <V3Icon name="eye" size={14} />
              </button>
              {password && (
                <div style={{ marginTop: 6, display: 'flex', gap: 4 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      style={{
                        flex: 1,
                        height: 3,
                        borderRadius: 2,
                        background: i <= strength.score ? strength.color : 'var(--v36-line)',
                        transition: 'background 0.2s',
                      }}
                    />
                  ))}
                  <span
                    className="v36-mono"
                    style={{
                      fontSize: 9,
                      color: strength.color,
                      letterSpacing: '0.10em',
                      marginLeft: 4,
                    }}
                  >
                    {strength.label}
                  </span>
                </div>
              )}
            </Field>

            <Field
              label="비밀번호 확인"
              hint={
                confirmPassword && password !== confirmPassword
                  ? '비밀번호가 일치하지 않습니다'
                  : undefined
              }
            >
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="비밀번호 재입력"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="v36-input"
                style={{
                  paddingRight: 40,
                  borderColor:
                    confirmPassword && password !== confirmPassword
                      ? 'var(--v36-error)'
                      : undefined,
                }}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? '숨기기' : '보기'}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: 22,
                  color: 'var(--v36-ink-4)',
                  padding: 4,
                }}
              >
                <V3Icon name="eye" size={14} />
              </button>
            </Field>

            {/* 약관 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  fontSize: 13,
                  color: 'var(--v36-ink-2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={agreeTerms}
                  onChange={(e) => setAgreeTerms(e.target.checked)}
                  style={{ accentColor: 'var(--v36-violet)', marginTop: 2 }}
                />
                <span>
                  <Link href="/terms" style={{ color: 'var(--v36-violet)', fontWeight: 500 }}>
                    이용약관
                  </Link>
                  에 동의합니다 <span style={{ color: 'var(--v36-error)' }}>*</span>
                </span>
              </label>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  fontSize: 13,
                  color: 'var(--v36-ink-2)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={agreePrivacy}
                  onChange={(e) => setAgreePrivacy(e.target.checked)}
                  style={{ accentColor: 'var(--v36-violet)', marginTop: 2 }}
                />
                <span>
                  <Link href="/privacy" style={{ color: 'var(--v36-violet)', fontWeight: 500 }}>
                    개인정보 처리방침
                  </Link>
                  에 동의합니다 <span style={{ color: 'var(--v36-error)' }}>*</span>
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="v36-btn v36-btn--neon v36-btn--lg"
              disabled={isLoading || !isTermsAgreed}
              style={{
                width: '100%',
                marginTop: 6,
                opacity: isLoading || !isTermsAgreed ? 0.5 : 1,
              }}
            >
              {isLoading ? (
                <>
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'v36-spin 0.8s linear infinite',
                    }}
                  />
                  <span>가입 중...</span>
                </>
              ) : (
                <>
                  <V3Icon name="bolt" size={14} />
                  <span>무료로 가입하기</span>
                </>
              )}
            </button>

            <div style={{ position: 'relative', margin: '14px 0 6px', textAlign: 'center' }}>
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '50%',
                  height: 1,
                  background: 'var(--v36-line)',
                }}
              />
              <span
                className="v36-mono"
                style={{
                  position: 'relative',
                  padding: '0 12px',
                  background: 'var(--v36-bg-2)',
                  fontSize: 10,
                  color: 'var(--v36-ink-4)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                }}
              >
                또는 소셜 계정
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button
                type="button"
                onClick={() => socialLogin('kakao')}
                className="v36-btn"
                style={{
                  background: '#FEE500',
                  color: '#3C1E1E',
                  borderColor: '#FEE500',
                  fontWeight: 600,
                }}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M12 3C6.477 3 2 6.463 2 10.691c0 2.724 1.8 5.119 4.508 6.49-.199.725-.72 2.625-.825 3.03-.13.503.185.495.389.36.16-.106 2.545-1.727 3.578-2.428.77.112 1.565.171 2.35.171 5.523 0 10-3.463 10-7.691C22 6.463 17.523 3 12 3z" />
                </svg>
                <span>카카오</span>
              </button>
              <button
                type="button"
                onClick={() => socialLogin('google')}
                className="v36-btn"
              >
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                <span>Google</span>
              </button>
            </div>
          </form>

          <div
            style={{
              marginTop: 24,
              paddingTop: 20,
              borderTop: '1px solid var(--v36-line)',
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--v36-ink-3)' }}>
              이미 계정이 있으신가요?{' '}
            </span>
            <Link
              href="/login"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--v36-violet)',
                textDecoration: 'none',
              }}
            >
              로그인 →
            </Link>
          </div>
        </div>

        <p
          className="v36-mono"
          style={{
            marginTop: 24,
            textAlign: 'center',
            fontSize: 10,
            color: 'var(--v36-ink-5)',
            letterSpacing: '0.10em',
          }}
        >
          © {new Date().getFullYear()} CLIPFLOW
        </p>
      </div>
    </main>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: 'var(--v36-ink-2)',
          letterSpacing: '-0.01em',
        }}
      >
        {label}
      </label>
      <div style={{ position: 'relative' }}>{children}</div>
      {hint && (
        <span
          style={{
            fontSize: 11,
            color: hint.includes('필요') || hint.includes('충족')
              ? 'var(--v36-ink-4)'
              : 'var(--v36-error)',
          }}
        >
          {hint}
        </span>
      )}
    </div>
  )
}
